/**
 * ポテンシャル分析設定の編集状態（FKV3-13111 / FKV3-13122）
 *
 * API のレスポンス（スネークケース・DB の形）と、画面が編集しやすい形を相互に変換する。
 */

import {
  CROSS_DEFAULT_PRODUCT_GROUP_LEVEL,
  DEFAULT_PRODUCT_GROUP_LEVEL,
  MAX_CROSS_AXES,
  type PotentialAnalysisCrossSetSetting,
  type PotentialAnalysisProductGroupTypeSetting,
} from '@/lib/types/feature/potential-analysis-setting';
import type { CrossAxisDraft } from './estimate';
import type { TypeSelection } from './ProductGroupTypeTable';

/** 設定の編集状態。保存済みとの比較で未保存判定に使う */
export interface EditState {
  selections: Record<string, TypeSelection>;
  /** 軸1〜3。長さは常に MAX_CROSS_AXES */
  crossAxes: CrossAxisDraft[];
  /**
   * 編集中の掛け合わせの組ID。読み込んだ組があればその値、無ければ null。
   *
   * 保存時に null なら新規採番する。画面は1組しか扱わないが、DBは複数組を持てるため、
   * 置き換える範囲をこのIDで限定する。
   */
  crossSetId: string | null;
  inactiveDays: string;
}

/** 未選択の軸で埋めた初期状態 */
export function emptyCrossAxes(): CrossAxisDraft[] {
  return Array.from({ length: MAX_CROSS_AXES }, () => ({
    productGroupTypeId: null,
    productGroupLevel: CROSS_DEFAULT_PRODUCT_GROUP_LEVEL,
  }));
}

/** 編集前の空の状態 */
export function emptyEditState(): EditState {
  return { selections: {}, crossAxes: emptyCrossAxes(), crossSetId: null, inactiveDays: '' };
}

/** 保存結果の掛け合わせを画面の軸1〜3へ流し込む。軸は前から詰まっている */
function toCrossAxes(saved: PotentialAnalysisCrossSetSetting | undefined): CrossAxisDraft[] {
  const axes = emptyCrossAxes();
  (saved?.axes ?? []).slice(0, MAX_CROSS_AXES).forEach((a, i) => {
    axes[i] = {
      productGroupTypeId: a.product_group_type_id,
      productGroupLevel: a.product_group_level,
    };
  });
  return axes;
}

/**
 * 画面に並べるタイプ一覧（`rows`）と、保存結果（`saved`）から編集状態を作る。
 *
 * 一覧と選択状態を分けて受けるのは、保存直後にマスタを引けなかったとき
 * （`master_available = false`）に一覧を差し替えず、選択状態だけを反映するため。
 * 一覧ごと差し替えると、出力対象にしていないタイプが画面から消えて選び直せなくなる。
 */
export function toEditState(
  rows: PotentialAnalysisProductGroupTypeSetting[],
  saved: PotentialAnalysisProductGroupTypeSetting[],
  crossSets: PotentialAnalysisCrossSetSetting[],
  days: number,
): EditState {
  const savedById = new Map(saved.map((t) => [t.product_group_type_id, t]));
  const selections: Record<string, TypeSelection> = {};
  for (const row of rows) {
    const t = savedById.get(row.product_group_type_id);
    selections[row.product_group_type_id] = {
      enabled: t?.enabled ?? false,
      productGroupLevel: t?.product_group_level ?? DEFAULT_PRODUCT_GROUP_LEVEL,
    };
  }
  return {
    selections,
    // 画面は先頭の1組だけを編集する（EDITABLE_CROSS_SETS）
    crossAxes: toCrossAxes(crossSets[0]),
    // 読み込んだ組を引き継ぐ。無ければ保存時に採番する
    crossSetId: crossSets[0]?.id ?? null,
    inactiveDays: String(days),
  };
}
